<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-hidden h-full w-full flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-4/5 container h-[85vh] overflow-scroll flex flex-col">
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="text-2xl text-gray-800 font-semibold">Insurance Claim Preview</h2>
        <button
          @click="closeModal"
          class="text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <div class="p-6 flex max-h-[80vh] overflow-y-auto">
        <div class="w-full" id="claim-preview">
          <div class="text-center mb-6">
            <h1 class="text-3xl font-bold">Insurance Claim</h1>
          </div>

          <div class="grid grid-cols-2 gap-4 mb-6">
            <div>
              <h3 class="text-xl font-semibold mb-2">Patient Information</h3>
              <p><strong>Name:</strong> {{ claim.patient_details?.full_name }}</p>
              <p><strong>ID Number:</strong> {{ claim.patient_details?.id_number }}</p>
              <p><strong>Phone:</strong> {{ claim.patient_details?.phone_number }}</p>
              <p><strong>Residence:</strong> {{ claim.patient_details?.residence }}</p>
            </div>
            <div>
              <h3 class="text-xl font-semibold mb-2">Claim Details</h3>
              <p><strong>Claim Type:</strong> {{ formatLabel(claim.claim_type) }}</p>
              <p><strong>Claimed Amount:</strong> {{ formatCurrency(claim.claimed_amount) }}</p>
              <p><strong>Status:</strong> {{ formatLabel(claim.claim_status) }}</p>
              <p><strong>Claim Date:</strong> {{ formatDate(claim.claimed_at) }}</p>
            </div>
          </div>

          <div class="mb-6">
            <h3 class="text-xl font-semibold mb-2">Insurance Information</h3>
            <p><strong>Provider:</strong>
              <span :class="claim.sha_claim_info?.is_sha_claim
                ? 'text-orange-600 font-medium'
                : 'text-blue-600 font-medium'">
                {{ claim.sha_claim_info?.is_sha_claim ? 'SHA' : 'M-TIBA' }}
              </span>
            </p>
            <p v-if="claim.sha_claim_info?.is_sha_claim"><strong>SHA Visit Number:</strong> {{ claim.sha_claim_info.sha_visit_number }}</p>
            <p v-if="claim.sha_claim_info?.is_sha_claim"><strong>SHA Claim Status:</strong> {{ formatLabel(claim.sha_claim_info.sha_claim_status) }}</p>
            <p v-else><strong>Member Number:</strong> {{ reservation?.policy_number || claim.insurance_details?.member_number || 'N/A' }}</p>
            <p v-if="claim.sha_claim_info?.is_sha_claim">SHA visit consent token and intervention codes are managed via the SHA eligibility flow.</p>
            <p v-else><strong>MTIBA Visit Code:</strong> {{ reservation?.visit_code || 'N/A' }}</p>
          </div>

          <div class="mb-6">
            <h3 class="text-xl font-semibold mb-2">Services Rendered</h3>
            <table class="w-full border-collapse border border-gray-300">
              <thead>
                <tr class="bg-gray-100">
                  <th class="border border-gray-300 px-4 py-2">Service</th>
                  <th class="border border-gray-300 px-4 py-2">Quantity</th>
                  <th class="border border-gray-300 px-4 py-2">Unit Price</th>
                  <th class="border border-gray-300 px-4 py-2">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="service in claim.services_rendered" :key="service.id">
                  <td class="border border-gray-300 px-4 py-2">{{ service.service_name }}</td>
                  <td class="border border-gray-300 px-4 py-2">{{ service.quantity }}</td>
                  <td class="border border-gray-300 px-4 py-2">{{ formatCurrency(service.unit_price) }}</td>
                  <td class="border border-gray-300 px-4 py-2">{{ formatCurrency(service.total_amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <h3 class="text-xl font-semibold mb-2">Bill Summary</h3>
              <p><strong>Total Amount:</strong> {{ formatCurrency(claim.bill_summary?.total_amount) }}</p>
              <p><strong>Bill Number:</strong> {{ claim.bill_summary?.bill_number }}</p>
              <p><strong>Bill Date:</strong> {{ formatDate(claim.bill_summary?.bill_date) }}</p>
            </div>
            <div>
              <h3 class="text-xl font-semibold mb-2">Visit Information</h3>
              <p><strong>Visit Code:</strong> {{ claim.visit?.VisitCode }}</p>
              <p><strong>Visit Date:</strong> {{ formatDate(claim.visit?.VisitStartTime || claim.visit?.created_at) }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-2 p-4 border-t mb-3">
        <button
          v-if="!claim.sha_claim_info?.is_sha_claim && claim.claim_status === 'pending_submission'"
          @click="submitToMtiba"
          :disabled="submitting"
          class="px-8 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          <span v-if="submitting">Submitting...</span>
          <span v-else>Submit to MTIBA</span>
        </button>
        <button
          v-else-if="claim.sha_claim_info?.is_sha_claim && claim.claim_status === 'pending_submission'"
          @click="dispatchShaClaim"
          :disabled="submitting"
          class="px-8 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
        >
          <span v-if="submitting">Dispatching...</span>
          <span v-else>Dispatch to SHA</span>
        </button>
        <button
          @click="printClaim"
          class="px-8 py-2 bg-custom-yellow text-white rounded"
        >
          Print
        </button>
        <button
          @click="closeModal"
          class="px-8 py-2 bg-gray-200 text-gray-800 rounded"
        >
          Close
        </button>
      </div>
    </div>

    <!-- Confirmation Dialog -->
    <div
      v-if="showConfirmDialog"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50"
      @click="cancelSubmitToMtiba"
    >
      <div class="bg-white rounded-lg shadow-xl w-96 mx-4" @click.stop>
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-lg font-medium text-gray-900">Confirm Submission</h3>
            </div>
          </div>
          <div class="mb-6">
            <p class="text-sm text-gray-600">
              Are you sure you want to submit this claim to MTIBA?
            </p>
            <div class="mt-2 p-3 bg-gray-50 rounded-md">
              <p class="text-sm text-gray-800">
                <strong>Patient:</strong> {{ claim.patient_details?.full_name }}<br>
                <strong>Amount:</strong> {{ formatCurrency(claim.claimed_amount) }}
              </p>
            </div>
          </div>
          <div class="flex justify-end space-x-3">
            <button
              @click="cancelSubmitToMtiba"
              class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              @click="confirmSubmitToMtiba"
              class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Yes, Submit to MTIBA
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "InsuranceClaimPreviewModal",
  props: {
    show: Boolean,
    claim: Object,
  },
  data() {
    return {
      reservation: null,
      submitting: false,
      showConfirmDialog: false,
    };
  },
  watch: {
    show: {
      handler(newShow) {
        if (newShow && this.claim && this.claim.visit_id) {
          this.fetchReservation();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchReservation() {
      if (!this.claim || !this.claim.visit_id) return;
      try {
        const response = await apiClient.fetchMtibaReservationByVisitId(this.claim.visit_id);
        if (response.success) {
          this.reservation = response.data;
        }
      } catch (error) {
        console.error('Failed to fetch reservation:', error);
      }
    },
    closeModal() {
      this.$emit("close");
    },
    printClaim() {
      // Get content before closing modal
      const printContent = document.getElementById('claim-preview').innerHTML;

      // Close the modal
      this.$emit('close');

      // Create hidden iframe for printing
      setTimeout(() => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(`
          <html>
            <head>
              <title>Insurance Claim</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
                h1, h2, h3 { color: #333; margin-bottom: 10px; }
                p { margin: 5px 0; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
                .mb-6 { margin-bottom: 20px; }
                .text-center { text-align: center; }
                .text-xl { font-size: 1.25rem; }
                .font-semibold { font-weight: 600; }
                .font-bold { font-weight: 700; }
                @media print {
                  body { margin: 0; padding: 10px; }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        iframeDoc.close();

        // Print the iframe content
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 300);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "KES",
      }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      const date = new Date(dateString);
      return date.toLocaleString("en-US", options);
    },
    formatLabel(str) {
      if (!str) return '';
      return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    async submitToMtiba() {
      this.showConfirmDialog = true;
    },

    async dispatchShaClaim() {
      if (!this.claim?.sha_claim_info?.sha_visit_number) {
        this.$toast.error('No SHA visit number found for this claim.');
        return;
      }
      try {
        this.submitting = true;
        const response = await apiClient.dispatchShaClaim(
          this.claim.sha_claim_info.sha_visit_number,
          {
            consent_token: this.claim.insurance_details?.consent_token || '',
            discharge_reason: 'RECOVERED'
          }
        );

        if (response.success) {
          this.$toast.success('SHA claim dispatched successfully');
          this.$emit('claim-updated');
          this.closeModal();
        } else {
          this.$toast.error(response.message || 'Failed to dispatch SHA claim.');
        }
      } catch (error) {
        console.error('Error dispatching SHA claim:', error);
        this.$toast.error('Failed to dispatch SHA claim.');
      } finally {
        this.submitting = false;
      }
    },

    async confirmSubmitToMtiba() {
      this.showConfirmDialog = false;

      try {
        this.submitting = true;
        const response = await apiClient.submitClaimToMtiba(this.claim.id);

        if (response.success) {
          this.$toast.success('Claim submitted to MTIBA successfully');

          // Check for MTIBA claim confirmation success
          if (response.data?.mtiba_claim_status?.success) {
            this.$toast.success(response.data.mtiba_claim_status.message);
          }

          this.$emit('claim-updated'); // Emit event to refresh parent component
          this.closeModal();
        } else {
          this.$toast.error(response.message || 'Failed to submit claim to MTIBA');
        }
      } catch (error) {
        console.error('Error submitting claim:', error);
        this.$toast.error('Failed to submit claim to MTIBA');
      } finally {
        this.submitting = false;
      }
    },

    cancelSubmitToMtiba() {
      this.showConfirmDialog = false;
    },
  },
};
</script>

<style lang="css" scoped>
@media print {
  body * {
    visibility: hidden;
  }
  #claim-preview, #claim-preview * {
    visibility: visible;
  }
  #claim-preview {
    position: absolute;
    left: 0;
    top: 0;
  }
}
</style>